import { useEffect, useState } from "react";
import { useLoop } from "../loop-provider";
import { Drawer, Text, Image, Button, Form, Icons, useDrawer, useToast } from "../../design";
import { translate } from "../../utils/translations";
import SelectProductSubscriptionDrawer from "./select-product-subscription";
import {
    handleError,
    useAxios,
    getCurrencyDisplay,
    calcDiscountSpString,
    loopSAtoString,
    transformMultilingualDataForAddProducts,
    getCurrencyDisplayInternationalized,
} from "../../utils/helpers";
import SelectStartDateDrawer from "./create-subscription-start-date";
import EditShippingAddressDrawer from "./create-subscription-edit-shipping-address";
import { format, addDays, subDays } from "date-fns";

const _ADD_DAYS = 2;

function CreateSubscriptionDrawer(props: any) {
    const drawers = useDrawer();
    const toasts = useToast();
    const [axios] = useAxios();
    const { myshopify_domain, store, customer_shopify_id, deliveryPreferences, localeRoute, locale, country } =
        useLoop();

    const [isLoading, setisLoading] = useState(false);

    const [selectedProduct, setselectedProduct] = useState(null);
    const [availableOptions, setavailableOptions] = useState([]);
    const [availablePaymentMethods, setavailablePaymentMethods] = useState([]);
    const [availableShippingAddresses, setavailableShippingAddresses] = useState([]);
    const [variantsDict, setvariantsDict] = useState({});

    const [selectedOptions, setselectedOptions]: Array<any> = useState([]);
    const [selectedVariant, setselectedVariant]: Array<any> = useState({});
    const [selectedSellingPlanId, setselectedSellingPlanId]: Array<any> = useState("");
    const [selectedSellingPlan, setselectedSellingPlan]: Array<any> = useState(null);
    const [chargeOffset, setchargeOffset] = useState(0);
    const [selectedStartDate, setselectedStartDate]: Array<any> = useState(
        addDays(addDays(new Date(), chargeOffset), _ADD_DAYS),
    );
    const [selectedPaymentMethodGraphQLId, setselectedPaymentMethodGraphQLId]: Array<any> = useState("");
    const [selectedShippingAddressIndex, setselectedShippingAddressIndex]: Array<any> = useState(0);

    const [quantity, setquantity] = useState("1");

    const loadProduct = async (product_shopify_id: any) => {
        setisLoading(true);
        try {
            const countryCode = (window as any)?.Shopify?.country || "__ALL__";
            let _params: any = {
                product_shopify_id: product_shopify_id,
                myshopify_domain: myshopify_domain,
                action: "create-subscription",
                countryCode: countryCode,
            };
            const [subscriptionData, storeDetails] = await Promise.all([
                axios.get(
                    `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/read_subscription_variants_by_pid?` +
                        new URLSearchParams(_params),
                ),
                axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/store_details`),
            ]);
            const { data } = subscriptionData;
            const storeDefaultLocale: string = storeDetails?.data.data.default_locale;

            if (storeDefaultLocale != null && storeDefaultLocale != locale)
                await transformMultilingualDataForAddProducts(
                    data.data,
                    myshopify_domain,
                    localeRoute,
                    locale,
                    storeDefaultLocale,
                );
            setselectedProduct(data.data);

            let _variantsDict = {};
            for (let variant of data?.data?.variants || []) {
                _variantsDict[variant.variant_title] = variant;
            }
            setvariantsDict(_variantsDict);

            let _selectedOptions = [];
            for (let option of data.data.product_options) {
                _selectedOptions.push(option.values[0]);
            }
            setselectedOptions(_selectedOptions);

            if (data.data.variants_count > 1) {
                setavailableOptions(data.data.product_options);
            } else {
                setavailableOptions([]);
            }

            const _selectedVariant = _variantsDict[_selectedOptions.join(" / ")];

            setselectedVariant(_selectedVariant);
            setselectedSellingPlanId(`${_selectedVariant?.selling_plans[0]?.id || ""}`);
            setselectedSellingPlan(_selectedVariant?.selling_plans?.[0] || null);
        } catch (error) {
            handleError(error, "components/drawers/create-subscription", "loadProduct", { product_shopify_id });
        }
        setisLoading(false);
    };

    const handleProductSelect = async (product: any) => {
        drawers.close();
        await loadProduct(product.product_shopify_id);
    };

    const toggleProductSelect = () => {
        drawers.open({
            component: SelectProductSubscriptionDrawer,
            props: {
                handleProductSelect: handleProductSelect,
                hideBackButton: selectedProduct == null,
            },
        });
    };

    const handleOptionChange = (i: number, value: string) => {
        let _selectedOptions = selectedOptions.slice();
        _selectedOptions[i] = value;
        setselectedOptions(_selectedOptions);
        setselectedVariant(variantsDict[_selectedOptions.join(" / ")]);
        setselectedSellingPlanId(`${variantsDict[_selectedOptions.join(" / ")]?.selling_plans[0]?.id || ""}`);
        setselectedSellingPlan(variantsDict[_selectedOptions.join(" / ")]?.selling_plans?.[0] || null);
    };

    const handleSelectedDateChange = (dateEpoch: number) => {
        setselectedStartDate(new Date(dateEpoch * 1000));
    };

    const toggleDateSelect = () => {
        drawers.open({
            component: SelectStartDateDrawer,
            props: {
                currentDate: selectedStartDate.getTime() / 1000,
                handleDateSelect: handleSelectedDateChange,
                billing_policy: {
                    anchor_type: selectedSellingPlan.billing_anchor_type,
                    anchor_day: selectedSellingPlan.billing_anchor_day,
                    anchor_month: selectedSellingPlan.billing_anchor_month,
                },
            },
        });
    };

    const handleShippingAddressEditConfirm = (address: any) => {
        let _availableShippingAddresses = availableShippingAddresses.slice();
        _availableShippingAddresses[selectedShippingAddressIndex] = {
            ...address,
        };
        setavailableShippingAddresses(_availableShippingAddresses);
    };

    const toggleEditShippingAddress = () => {
        drawers.open({
            component: EditShippingAddressDrawer,
            props: {
                shippingAddress: availableShippingAddresses[selectedShippingAddressIndex],
                handleConfirm: handleShippingAddressEditConfirm,
            },
        });
    };

    const handleConfirm = async () => {
        setisLoading(true);
        try {
            const _startDate = subDays(selectedStartDate, chargeOffset);
            let body = {
                products: [
                    {
                        variant_shopify_id: selectedVariant.variant_shopify_id,
                        quantity: quantity,
                    },
                ],
                sellingPlan_id: selectedSellingPlan.id,
                start_date: _startDate.toISOString(),
                paymentMethod_graphql_id: selectedPaymentMethodGraphQLId,
                shipping_address: availableShippingAddresses[selectedShippingAddressIndex],
            };
            const { data }: any = await axios.post(
                `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/create`,
                body,
            );
            toasts.open({
                message: data.message,
                type: data.success ? "success" : "failure",
                time: 10000,
            });
            if (data.success) {
                props.toggleActive();
                props.reloadContracts();
            }
        } catch (error) {
            handleError(error, "components/drawers/create-subscription", "handleConfirm", {
                myshopify_domain,
                customer_shopify_id,
            });
        }
        setisLoading(false);
    };

    const getDisableBeforeDates = () => {
        let _chargeOffset = undefined;
        for (const _pref of deliveryPreferences) {
            if (
                _pref.defaultPreferenceKey === "delivery_scheduled_order" &&
                _pref.storePreferenceEnabled &&
                _pref.inputValueInt
            ) {
                _chargeOffset = _pref.inputValueInt;
                break;
            } else {
                _chargeOffset = 0;
            }
        }
        setchargeOffset(_chargeOffset);
        setselectedStartDate(addDays(addDays(new Date(), _chargeOffset), _ADD_DAYS));
    };

    const loadData = async () => {
        setisLoading(true);
        try {
            const [{ data: pmData }, { data: saData }] = await Promise.all([
                axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/available_payment_methods`),
                axios.get(`${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/available_shipping_addresses`),
            ]);
            setavailablePaymentMethods(pmData.data);
            if (pmData.data.length > 0) {
                setselectedPaymentMethodGraphQLId(pmData.data[0].admin_graphql_api_id);
            }
            if (saData.data.length > 0) {
                setavailableShippingAddresses(saData.data);
            } else {
                setavailableShippingAddresses([
                    {
                        first_name: "",
                        last_name: "",
                        company: "",
                        phone: "",
                        address1: "",
                        address2: "",
                        zip: "",
                        country_code: "",
                        province_code: "",
                        city: "",
                    },
                ]);
            }
            setselectedShippingAddressIndex(0);
        } catch (error) {
            handleError(error, "components/drawers/create-subscription", "loadData", {
                myshopify_domain,
                customer_shopify_id,
            });
        }
        setisLoading(false);
    };

    const isDataValid = () => {
        if (!selectedVariant) {
            return false;
        }
        if (!availableShippingAddresses[selectedShippingAddressIndex]) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (!selectedProduct) {
            toggleProductSelect();
        }
        getDisableBeforeDates();
        loadData();
    }, []);

    return (
        <Drawer
            title={translate("subscription_create_drawer_title")}
            active={props.active}
            toggleActive={props.toggleActive}
            id="subscription-create-title"
        >
            <Drawer.Body>
                {isLoading ? (
                    <Icons.Spinner />
                ) : (
                    <>
                        {selectedProduct ? (
                            <>
                                <div className="border-bottom pb-2">
                                    <Text.P1>{translate("subscription_create_product_title")}</Text.P1>
                                    <div className="d-flex my-3">
                                        <Image.ProductImage className="me-3" src={selectedProduct.image} />
                                        <div className="d-flex flex-column justify-content-between align-items-start">
                                            <div>
                                                <Text.P1>{selectedProduct.product_title}</Text.P1>
                                                <Text.P1>
                                                    {selectedVariant
                                                        ? getCurrencyDisplayInternationalized(
                                                              store.currency,
                                                              locale,
                                                              country,
                                                              selectedVariant.price,
                                                          )
                                                        : "-"}
                                                </Text.P1>
                                            </div>
                                            <Button size="sm" variant="underline" onClick={toggleProductSelect}>
                                                {translate("subscription_create_change_product_btn")}
                                            </Button>
                                        </div>
                                    </div>

                                    {availableOptions.length > 0 ? (
                                        <>
                                            <Text.P1>{translate("subscription_create_product_options")}</Text.P1>
                                            <div className="d-flex my-1">
                                                {availableOptions.map((option: any, i: number) => {
                                                    return (
                                                        <Form.Select
                                                            className="mt-1"
                                                            key={i}
                                                            label={option.name}
                                                            value={selectedOptions[i]}
                                                            onChange={(event: any) =>
                                                                handleOptionChange(i, event.target.value)
                                                            }
                                                            options={option.values.map((val: string) => {
                                                                return {
                                                                    label: val,
                                                                    value: val,
                                                                };
                                                            })}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </>
                                    ) : null}

                                    {selectedVariant ? (
                                        <div className="mt-3" style={{ maxWidth: "150px" }}>
                                            <Text.P1>{translate("subscription_create_quantity_label")}</Text.P1>
                                            <div className="d-flex my-2">
                                                <Button
                                                    className="rounded-0 rounded-start d-flex align-items-center px-2 py-0"
                                                    variant="input"
                                                    disabled={parseInt(quantity) <= 1}
                                                    onClick={() => {
                                                        let _quantity = parseInt(quantity) - 1;
                                                        setquantity(_quantity + "");
                                                    }}
                                                >
                                                    <Icons.Minus />
                                                </Button>
                                                <Form.Input
                                                    type="number"
                                                    className="rounded-0 border-start-0 border-end-0 text-center"
                                                    value={quantity}
                                                    onChange={(event: any) => {
                                                        setquantity(event.target.value);
                                                    }}
                                                />
                                                <Button
                                                    className="rounded-0 rounded-end d-flex align-items-center px-2 py-0"
                                                    variant="input"
                                                    onClick={() => {
                                                        let _quantity = parseInt(quantity) + 1;
                                                        setquantity(_quantity + "");
                                                    }}
                                                >
                                                    <Icons.Plus />
                                                </Button>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                                {selectedVariant ? (
                                    <>
                                        <div className="my-2">
                                            <Form.Select
                                                className="mt-1"
                                                label={translate("subscription_create_drawer_frequency_label")}
                                                value={selectedSellingPlanId}
                                                onChange={(event: any) => {
                                                    setselectedSellingPlanId(event.target.value);
                                                    setselectedSellingPlan(
                                                        selectedVariant.selling_plans.filter(
                                                            (item: any) => item.id.toString() == event.target.value,
                                                        )[0],
                                                    );
                                                }}
                                                options={selectedVariant.selling_plans.map((sp: any) => {
                                                    const _disc = calcDiscountSpString(
                                                        sp.pricing_policies,
                                                        0,
                                                        getCurrencyDisplay(store.currency),
                                                    );
                                                    let _discString = _disc
                                                        ? _disc.type == "PERCENTAGE" || _disc.type == "FIXED_AMOUNT"
                                                            ? _disc.value + " " + translate("off_label")
                                                            : "@ " + _disc.value
                                                        : null;
                                                    return {
                                                        label:
                                                            sp.loopSellingPlanGroupName +
                                                            " - " +
                                                            sp.name +
                                                            (_discString ? " (" + _discString + ")" : ""),
                                                        value: sp.id.toString(),
                                                    };
                                                })}
                                            />
                                        </div>
                                        <div className="my-2">
                                            <Form.Input
                                                className="mt-1"
                                                label={translate("subscription_create_drawer_start_date_label")}
                                                value={format(selectedStartDate, "LLL d, yyyy")}
                                                onClick={toggleDateSelect}
                                            />
                                        </div>
                                        <div className="my-2">
                                            <Form.Select
                                                className="mt-1"
                                                label={translate("subscription_create_drawer_payment_method_label")}
                                                value={selectedPaymentMethodGraphQLId}
                                                onChange={(event: any) => {
                                                    setselectedPaymentMethodGraphQLId(event.target.value);
                                                }}
                                                options={availablePaymentMethods.map((pm: any) => {
                                                    let _str = "";
                                                    let _instrumentType = pm.instrument_type;
                                                    let _instrument = pm.payment_instrument;

                                                    if (
                                                        _instrumentType == "CustomerCreditCard" ||
                                                        _instrumentType == "CustomerRemoteCreditCard" ||
                                                        _instrumentType == "AuthorizeNetCustomerPaymentProfile" ||
                                                        _instrumentType == "CustomerRemoteBraintreeCreditCard"
                                                    ) {
                                                        let _brand = _instrument.brand;
                                                        let _lastDigits = _instrument.virtualLastDigits
                                                            ? _instrument.virtualLastDigits
                                                            : _instrument.lastDigits
                                                              ? _instrument.lastDigits
                                                              : _instrument.last_digits
                                                                ? _instrument.last_digits
                                                                : "";
                                                        let _expiryMonth = _instrument.expiryMonth
                                                            ? _instrument.expiryMonth
                                                            : _instrument.month
                                                              ? _instrument.month
                                                              : "";
                                                        let _expiryYear = _instrument.expiryYear
                                                            ? _instrument.expiryYear % 100
                                                            : _instrument.year
                                                              ? _instrument.year % 100
                                                              : "";
                                                        _str =
                                                            translate("payment_card_details_text", {
                                                                card_brand: _brand,
                                                                last_digits: _lastDigits,
                                                            }) +
                                                            ", " +
                                                            translate("payment_card_expiry_text", {
                                                                expiryMonth: _expiryMonth,
                                                                expiryYear: _expiryYear,
                                                            });
                                                    } else if (_instrumentType == "CustomerShopPayAgreement") {
                                                        let _brand = "ShopPay";
                                                        let _lastDigits = _instrument.virtualLastDigits
                                                            ? _instrument.virtualLastDigits
                                                            : _instrument.lastDigits
                                                              ? _instrument.lastDigits
                                                              : _instrument.last_digits
                                                                ? _instrument.last_digits
                                                                : "";
                                                        let _expiryMonth = _instrument.expiryMonth
                                                            ? _instrument.expiryMonth
                                                            : _instrument.month
                                                              ? _instrument.month
                                                              : "";
                                                        let _expiryYear = _instrument.expiryYear
                                                            ? _instrument.expiryYear % 100
                                                            : _instrument.year
                                                              ? _instrument.year % 100
                                                              : "";
                                                        _str =
                                                            translate("payment_card_details_text", {
                                                                card_brand: _brand,
                                                                last_digits: _lastDigits,
                                                            }) +
                                                            ", " +
                                                            translate("payment_card_expiry_text", {
                                                                expiryMonth: _expiryMonth,
                                                                expiryYear: _expiryYear,
                                                            });
                                                    } else if (
                                                        _instrumentType == "CustomerPaypalBillingAgreement" ||
                                                        _instrumentType == "PaypalBillingAgreement"
                                                    ) {
                                                        if (_instrument.paypalAccountEmail) {
                                                            _str = translate("paypal_account_with_email_text", {
                                                                email: _instrument.paypalAccountEmail,
                                                            });
                                                        } else {
                                                            _str = translate("paypal_account_without_email_text");
                                                        }
                                                    }
                                                    return {
                                                        label: _str,
                                                        value: pm.admin_graphql_api_id,
                                                    };
                                                })}
                                            />
                                        </div>
                                        <div className="my-2">
                                            <Form.Select
                                                className="mt-1"
                                                label={
                                                    <div className="d-flex justify-content-between">
                                                        <div>
                                                            {translate(
                                                                "subscription_create_drawer_shipping_address_label",
                                                            )}
                                                        </div>
                                                        <Button
                                                            className="me-2"
                                                            variant="underline"
                                                            size="sm"
                                                            onClick={toggleEditShippingAddress}
                                                        >
                                                            {translate(
                                                                "subscription_create_drawer_edit_shipping_address_btn",
                                                            )}
                                                        </Button>
                                                    </div>
                                                }
                                                value={selectedShippingAddressIndex}
                                                onChange={(event: any) => {
                                                    setselectedShippingAddressIndex(event.target.value);
                                                }}
                                                options={availableShippingAddresses.map((sa: any, i: number) => {
                                                    return {
                                                        label: loopSAtoString(sa),
                                                        value: i,
                                                    };
                                                })}
                                            />
                                        </div>
                                    </>
                                ) : null}
                            </>
                        ) : null}
                    </>
                )}
            </Drawer.Body>
            <Drawer.Footer>
                <div className="d-flex flex-column">
                    <Button size="lg" className="m-2" onClick={handleConfirm} disabled={!isDataValid() || isLoading}>
                        {translate("subscription_create_drawer_start_btn")}
                    </Button>
                    <Button
                        size="lg"
                        variant="underline"
                        className="m-2"
                        onClick={props.toggleActive}
                        disabled={isLoading}
                    >
                        {translate("subscription_create_drawer_close_btn")}
                    </Button>
                </div>
            </Drawer.Footer>
        </Drawer>
    );
}

export default CreateSubscriptionDrawer;
